import { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../api/config';
import DashboardLayout from '../components/DashboardLayout';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  // IconButton, 
  // Tooltip,
  Button,
  TextField,
  Alert
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { getDateInputProps } from '../utils/dateConstraints';

const ItemSalesByCustomer = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingUrl, setDownloadingUrl] = useState(null);

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Initialize date filters with today's date
  const today = getTodayDate();
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);

  // Extract date from path or csv_full_url
  const extractDate = (item) => {
    if (!item) return '-';
    
    // Try to extract from path
    if (item.path) {
      const match = item.path.match(/(\d{4}-\d{2}-\d{2})/);
      if (match) return match[1];
    }
    
    // Try to extract from csv_full_url
    if (item.csv_full_url) {
      const match = item.csv_full_url.match(/(\d{4}-\d{2}-\d{2})/);
      if (match) return match[1];
    }
    
    return '-';
  };

  // Fetch data from API
  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await api.get('/item_sales_by_cus', {
        params: {
          start_date: startDate,
          end_date: endDate
        }
      });
      
      setData(response.data);
    } catch (err) {
      console.error('Error fetching item sales by customer:', err);
      setError(err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to load item sales by customer data');
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate]);

  // Fetch data when dates change
  useEffect(() => {
    if (startDate && endDate) {
      fetchData();
    }
  }, [startDate, endDate, fetchData]);

  // Handle download CSV
  const handleDownloadCSV = async (csvUrl) => {
    if (!csvUrl || downloadingUrl) return;
    
    setDownloadingUrl(csvUrl);
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(csvUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/csv'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to download CSV: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from URL
      const urlParts = csvUrl.split('/');
      const filename = urlParts[urlParts.length - 1] || 'item_sales.csv';
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading CSV:', err);
      setError(err.message || 'Failed to download CSV file');
    } finally {
      setDownloadingUrl(null);
    }
  };

  // Extract CSV URL from data (keeps compatibility with existing API shape)
  const csvUrl = useMemo(() => {
    if (!data) return null;
    
    // If API returns an array, take first item's csv_full_url
    if (Array.isArray(data) && data.length > 0) {
      return data[0]?.csv_full_url || null;
    }
    
    // If it's a single object
    if (typeof data === 'object' && data !== null) {
      return data.csv_full_url || null;
    }
    
    return null;
  }, [data]);

  return (
    <DashboardLayout>
      <Box sx={{ p: 1.5 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1.5, fontSize: '1.25rem' }}>
          Item Sales by Customer
        </Typography>

        <Box
          sx={{
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            width: '100%',
            flexWrap: 'wrap'
          }}
        >
          {/* Date range + refresh + download */}
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              type="date"
              label="Start Date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={getDateInputProps()}
              sx={{ 
                width: { xs: 140, sm: 160 },
                fontSize: '0.875rem',
                '& .MuiInputBase-root': {
                  height: '36px',
                  fontSize: '0.875rem'
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.875rem'
                }
              }}
            />
            <TextField
              type="date"
              label="End Date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={getDateInputProps()}
              sx={{ 
                width: { xs: 140, sm: 160 },
                fontSize: '0.875rem',
                '& .MuiInputBase-root': {
                  height: '36px',
                  fontSize: '0.875rem'
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.875rem'
                }
              }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={
                downloadingUrl
                  ? <CircularProgress size={14} />
                  : <DownloadIcon sx={{ fontSize: '1rem' }} />
              }
              onClick={() => csvUrl && handleDownloadCSV(csvUrl)}
              disabled={downloadingUrl || !csvUrl || isLoading}
              sx={{ fontSize: '0.75rem', py: 0.75, px: 1.5, whiteSpace: 'nowrap' }}
            >
              {downloadingUrl ? 'Downloading...' : 'Download CSV'}
            </Button>
          </Box>
        </Box>

        {isLoading && (
          <Box
            sx={{
              mt: 1,
              mb: 1,
              px: 1.5,
              py: 1.25,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'info.light',
              background: 'linear-gradient(90deg, rgba(2, 136, 209, 0.08), rgba(2, 136, 209, 0.02))',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              '@keyframes loadingDots': {
                '0%': { content: '"."' },
                '33%': { content: '".."' },
                '66%': { content: '"..."' },
                '100%': { content: '"."' }
              }
            }}
          >
            <CircularProgress size={18} />
            <Typography variant="body2" sx={{ color: 'info.dark', fontWeight: 500 }}>
              Data is being fetched, please wait
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  width: '1.2em',
                  textAlign: 'left',
                  '&::after': {
                    content: '"..."',
                    animation: 'loadingDots 1.2s infinite'
                  }
                }}
              />
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 1.5, fontSize: '0.875rem' }}>
                      {String(error?.message || error)}
          </Alert>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default ItemSalesByCustomer;

